// The products of one content type of a company, from the visibility matrix (SalesMatrix) — or all
// of them, in its "All products" tab (ProductsAvailability): what
// pausing that type takes off the store, or turning it on brings back. A search on top, then the
// products with their picture and name, a page at a time (vendor {products}, archived ones left
// out, as the matrix counts them). The store's own listing is not asked: it leaves out what is
// paused, so a paused type would show nothing.
//
// Each product's "Available in store" is the product dialog's Enabled (StoreProduct.enabled): ticked
// here it is written the same way, and a change made anywhere — here, the product dialog, another
// member's screen — is heard on the company's realtime room (submissions:product-availability).
import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import DataTable from "aldoo-ra/DataTable"
import Input from "aldoo-ra/Input"
import Typography from "aldoo-ra/Typography"
import { showNotification } from "aldoo-ra/Notification"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { ListVendorProducts, SetProductEnabled } from "../AdminShared/api"
import useSubmissionEvents from "../Vendor/useSubmissionEvents"
import { Thumb, Chip, Toggle } from "../AdminShared/ui"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { COMPANY } from "../Vendor/texts/bundles"

// catalog-store EPublishStatus.Published
const PUBLISHED = 2

/**
 * The company's products with their store availability: a search, then the table (picture, name,
 * the id behind a chip, published or not, "Available in store"). `contentType`: one type's only (the
 * dialog below); without it, the whole catalog (the matrix's "All products" tab), each row naming
 * its type from `typeNames` ({ contentId: name }).
 */
// what keeps a product off the store above its own checkbox (SalesMatrix overrideOf), as said in its place
const OVERRIDE_LABELS = {
  catalog: "Paused with the whole catalog",
  kind: "Paused by the delivery type",
  auto: "Paused: no courier",
  type: "Paused by its product type",
  held: "Held by Aldoo",
}

export function ProductsAvailability({ orgId, contentType = null, typeNames = null, overrideOf = null, autoFocus = false }) {
  const t = usePortalTexts(COMPANY)
  const [term, setTerm] = useState("")
  // pid → enabled, what changed since the page was read (ticked here or heard from elsewhere)
  const [enabledNow, setEnabledNow] = useState({})
  const [saving, setSaving] = useState({})
  // the products whose id is shown in full (a "Product ID" chip until clicked)
  const [shownIds, setShownIds] = useState({})
  const toggleId = (pid) => setShownIds((now) => ({ ...now, [pid]: !now[pid] }))
  useSubmissionEvents({
    orgId: orgId || null,
    enabled: !!orgId,
    events: ["submissions:product-availability"],
    onEvent: ({ pid, enabled }) => pid && setEnabledNow((now) => ({ ...now, [pid]: enabled !== false })),
  })
  const isEnabled = (product) => (product.pid in enabledNow ? enabledNow[product.pid] : product.enabled !== false)

  const setAvailable = async (product, enabled) => {
    const before = isEnabled(product)
    setEnabledNow((now) => ({ ...now, [product.pid]: enabled }))
    setSaving((now) => ({ ...now, [product.pid]: true }))
    const response = await SetProductEnabled(product.pid, enabled).catch((error) => ({ error: error?.message || "request_failed" }))
    setSaving((now) => ({ ...now, [product.pid]: false }))
    if (!response?.error) {
      showNotification({ message: enabled ? t("Available in store: %s", product.name || product.pid) : t("Off the store: %s", product.name || product.pid), className: "bg-admin_success text-white" })
      return
    }
    setEnabledNow((now) => ({ ...now, [product.pid]: before }))
    showNotification({ message: t`Request failed`, className: "bg-admin_error text-white" })
  }

  const apiMethod = ({ page = 1, itemsPerPage = 10 }) =>
    ListVendorProducts({ orgId, contentType, archived: false, search: term.trim() || undefined, page, itemsPerPage, sort: { field: "updated", order: "desc" } })

  const typeColumn = !contentType && typeNames
    ? [{ id: "profile", title: t`Type`, headerClassName: "w-[170px]", render: (value) => <span className="text-sm text-admin_text_secondary">{typeNames[value?.id] || value?.name || "—"}</span> }]
    : []

  const columns = [
    {
      id: "name",
      title: t`Product`,
      render: (value, product) => (
        <div className="flex items-center gap-3 min-w-0">
          <Thumb src={product.image} name={value} className="w-12 h-12 rounded-md shrink-0" />
          <div className="min-w-0">
            {/* a phone's card: the whole name, wrapped; the table's row: one line */}
            <div className="break-words md:truncate text-sm font-semibold text-admin_text">{value}</div>
            {/* the id folded to a chip; a click shows it in full (and folds it again) */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                toggleId(product.pid)
              }}
              title={product.pid}
              className={shownIds[product.pid] ? "block max-w-full truncate text-left text-xs text-admin_text_secondary font-mono hover:text-admin_text" : "mt-0.5 inline-flex h-5 items-center rounded-full bg-admin_neutral_1 px-2 text-[11px] font-semibold text-admin_text_secondary hover:bg-admin_neutral_2"}
              data-testid="type-product-id"
            >
              {shownIds[product.pid] ? product.pid : t`Product ID`}
            </button>
          </div>
        </div>
      ),
    },
    ...typeColumn,
    {
      // what is not on the store whatever this type's switch says: not through the review yet
      id: "publishStatus",
      title: "",
      headerClassName: "w-[150px]",
      render: (value) => (value !== PUBLISHED ? <Chip className="bg-admin_neutral_1 text-admin_text_secondary">{t`Not published`}</Chip> : null),
    },
    {
      // the product dialog's Enabled: on, it is offered in the store; off, it leaves it
      id: "enabled",
      title: t`Available in store`,
      headerClassName: "w-[200px] whitespace-nowrap",
      // something above it keeps it off (its type, its delivery type, the catalog): not shown as
      // available — what overrides it is said instead of the checkbox
      render: (_, product) => {
        const override = overrideOf?.(product.profile?.id || contentType)
        if (override)
          return (
            <span data-testid="type-product-override" data-override={override}>
              <Chip className="bg-amber-100 text-amber-900">{t(OVERRIDE_LABELS[override])}</Chip>
            </span>
          )
        return (
          <span onClick={(e) => e.stopPropagation()}>
            <Toggle checked={isEnabled(product)} disabled={!!saving[product.pid]} onChange={(checked) => setAvailable(product, checked)} data-testid="type-product-available" data-pid={product.pid} />
          </span>
        )
      },
    },
  ]

  return (
    <div className="flex flex-col gap-4" data-testid="products-availability">
      <div className="w-full" data-testid="type-products-search">
        <Input
          variant="admin"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t`Search products…`}
          className="w-full"
          autoFocus={autoFocus}
          icon={<MagnifyingGlassIcon className="w-4 h-4 text-admin_text_secondary" />}
        />
      </div>
      {/* the table's frame from md; a phone's cards have their own borders */}
      <div className="md:border md:border-admin_bordered md:rounded-lg">
        <DataTable
          variant="admin_browser"
          preferredHeight="60vh"
          apiMethod={apiMethod}
          showSearch={false}
          renders={columns}
          refreshKey={term}
          itemsPerPage={10}
          mobileCards
          paginationOfLabel={t`of`}
          styleOverrides={{ container: "md:h-auto" }}
          noData={<Typography variant="admin_body1">{t`No products found`}</Typography>}
        />
      </div>
    </div>
  )
}

export default function TypeProductsDialog({ orgId, contentType, name, overrideOf = null, onClose }) {
  return (
    <Modal
      isOpen
      onClose={onClose}
      variant="admin"
      closeOnEscape
      styleOverrides={{ container: "w-[min(94vw,760px)] max-h-full md:max-h-[calc(100vh-2rem)] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg overflow-hidden" }}
    >
      <div className="flex flex-col gap-4 p-5 pt-1" data-testid="type-products-dialog">
        <div className="text-lg font-semibold text-admin_text">{name}</div>
        <ProductsAvailability orgId={orgId} contentType={contentType} overrideOf={overrideOf} autoFocus />
      </div>
    </Modal>
  )
}
